import React, { useState, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';

import AddOrEditCompany from '../../Console/CompanyManagement/Companies/edit/AddOrEditCompany';

import AllCompanies from './AllCompanies';
import CompaniesListHeader from './CompaniesListHeader';

import useDebouncedSearchWithHistory from '../../../../hooks/useDebouncedSearchWithHistory';
import AdminService from '../../../../services/AdminService';
import classes from './styles.module.scss';

export default function CompaniesList() {
  const [activeSortOption, setActiveSortOption] = useState({});
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddCompanyVisible, setIsAddCompanyVisible] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isFullScreenModeEnabled, setIsFullScreenModeEnabled] = useState(true);
  const [view, setView] = useState('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditCompanyVisible, setIsEditCompanyVisible] = useState(false);
  const [activeFilterSwitchOption, setActiveFilterSwitchOption] = useState({
    label: 'Active',
    value: 'Active',
  });
  const { debouncedSearchTerm, searchHistory } = useDebouncedSearchWithHistory(
    searchTerm,
    'companies'
  );

  const {
    data: companies,
    refetch: refetchCompanies,
    isFetching,
  } = useQuery({
    queryKey: [
      'companies',
      activeSortOption,
      debouncedSearchTerm,
      sortOrder,
      currentPage,
      activeFilterSwitchOption,
    ],
    queryFn: () =>
      AdminService.getCompaniesList({
        term: debouncedSearchTerm,
        orderBy: activeSortOption.value,
        sortOrder,
        pageNumber: currentPage,
        pageSize: 20,
        Filters: activeFilterSwitchOption
          ? `company.status:eq:${activeFilterSwitchOption.value}`
          : '',
      }),
    keepPreviousData: true,
  });

  const filterSwitchOptions = useMemo(() => {
    return [
      {
        label: 'Active',
        value: 'Active',
        count: companies?.data?.counts?.activeCount,
      },
      {
        label: 'Prospect',
        value: 'Prospect',
        count: companies?.data?.counts?.prospectCount,
      },
      {
        label: 'Inactive',
        value: 'Inactive',
        count: companies?.data?.counts?.inactiveCount,
      },
    ];
  }, [companies?.data?.counts]);

  const toggleCandidateSelection = (candidateId) => {
    setSelectedRows((prevRows) => {
      if (prevRows.includes(candidateId)) {
        return prevRows.filter((row) => row !== candidateId);
      }
      return [...prevRows, candidateId];
    });
  };

  const showAddCompanyPopup = () => {
    setIsAddCompanyVisible(true);
  };

  const showEditCompanyPopup = () => {
    setIsEditCompanyVisible(true);
  };

  return (
    <div
      className={classNames(classes.CompaniesList, {
        [classes.tableView]: view === 'list',
        [classes.fullScreen]: isFullScreenModeEnabled,
      })}
    >
      <div className={classes.headerContainer}>
        <CompaniesListHeader
          totalCount={companies?.totalCount}
          showAddCompanyPopup={showAddCompanyPopup}
        />
      </div>
      <div className={classes.allCompaniesContainer}>
        <AllCompanies
          debouncedSearchTerm={debouncedSearchTerm}
          showAddCompanyPopup={showAddCompanyPopup}
          showEditCompanyPopup={showEditCompanyPopup}
          isFullScreenModeEnabled={isFullScreenModeEnabled}
          setIsFullScreenModeEnabled={setIsFullScreenModeEnabled}
          setSortOrder={setSortOrder}
          sortOrder={sortOrder}
          // sortOptions={sortOptions}
          companies={companies?.data?.data || []}
          refetch={refetchCompanies}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          toggleCandidateSelection={toggleCandidateSelection}
          activeSortOption={activeSortOption}
          setActiveSortOption={setActiveSortOption}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          searchHistory={searchHistory}
          setIsCandidatePopupVisible={setIsAddCompanyVisible}
          view={view}
          setView={setView}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={companies?.totalPages}
          filterSwitchOptions={filterSwitchOptions}
          activeFilterSwitchOption={activeFilterSwitchOption}
          setActiveFilterSwitchOption={setActiveFilterSwitchOption}
          activeCandidateFilter="All"
          setActiveCandidateFilter={() => {}}
          candidatesCount={companies?.totalCount}
          isFetching={isFetching}
        />
      </div>
      <AddOrEditCompany
        isVisible={isAddCompanyVisible}
        handleClose={() => setIsAddCompanyVisible(false)}
        refetch={refetchCompanies}
        showAgain={() => setIsAddCompanyVisible(true)}
        isAdd
        companies={[]}
      />
      <AddOrEditCompany
        isVisible={isEditCompanyVisible}
        handleClose={() => setIsEditCompanyVisible(false)}
        refetch={refetchCompanies}
        showAgain={() => setIsEditCompanyVisible(true)}
        isEdit
        selectedRows={selectedRows}
        companies={
          companies?.data?.data.map((company) => company.company) || []
        }
      />
    </div>
  );
}
